import { io } from "socket.io-client";
import { spawnPlayerLabel } from "./ui/damage-text";
import { playSound } from "./utils/sound-utils";
import {
  Item,
  ItemDefinition,
  PlayerData,
  SquadMemberDetail,
  SquadUpdateData,
  TILE_SIZE,
} from "./models/common";
import { LootBox } from "./models/loot-box";
import { WindowManager } from "./ui/window-manager";
import { BaseGameScene } from "./scenes/base-game-scene";
import { BaseScene } from "./scenes/base-scene";
import { Interactable } from "./managers/interaction-manager";
import {
  registerItemListeners,
  unregisterItemListeners,
} from "./network/item-listeners";
import {
  registerPlayerListeners,
  unregisterPlayerListeners,
  type PlayerListenerOptions,
} from "./network/player-listeners";

// Re-export new listeners
export {
  registerItemListeners,
  unregisterItemListeners,
  registerPlayerListeners,
  unregisterPlayerListeners,
  type PlayerListenerOptions,
};

// Another test
// Register inventory and player info listeners for a scene
export async function connectToServer() {
  // Create socket connection if not already present
  if (!window.socket) {
    // Priority: VITE_SERVER_URL env var (baked in at build time) > sessionStorage override > production default
    let SERVER_URL =
      import.meta.env.VITE_SERVER_URL ||
      "https://mobile-extraction-server-eu.onrender.com";

    // Check if dev server URL is stored in sessionStorage
    // sessionStorage resets on app restart, so dev mode doesn't persist
    const devServerUrl = sessionStorage.getItem("devServerUrl");
    if (devServerUrl) {
      SERVER_URL = devServerUrl;
      console.info("Using dev server:", SERVER_URL);
    } else if (import.meta.env.VITE_SERVER_URL) {
      console.info("Using server URL from environment:", SERVER_URL);
    }

    window.socket = io(SERVER_URL);
  }

  const maxWaitMs = 120000; // 2 minutes
  const pollIntervalMs = 100;
  let waitedMs = 0;

  return new Promise<boolean>((resolve) => {
    function checkConnected() {
      if (window.socket && window.socket.connected) {
        console.info("Connected to server");
        resolve(true);
      } else if (waitedMs >= maxWaitMs) {
        console.error("Failed to connect to server");
        resolve(false);
      } else {
        waitedMs += pollIntervalMs;
        setTimeout(checkConnected, pollIntervalMs);
      }
    }
    checkConnected();
  });
}

/**
 * Helper function to update player data and refresh inventory window if visible.
 * Used by multiple scenes (BaseScene, MainScene, MarketScene) for consistent behavior.
 */
export function updatePlayerDataAndRefreshInventory(
  playerData: PlayerData,
  windowManager: WindowManager | undefined,
) {
  if (window.player) {
    const existingRooms = window.player.data?.rooms;
    window.player.data = {
      ...playerData,
      rooms: playerData.rooms ?? existingRooms ?? [],
    };
  }
  if (windowManager) {
    // Refresh inventory window if visible
    if (windowManager.isInventoryWindowVisible()) {
      windowManager.showInventoryWindow();
    }
    // Refresh player info widget to show updated credits
    windowManager.showPlayerInfoWidget();
  }
}

/**
 * Registers listeners for inventory-items and player-info socket events.
 * Expects scene to have a 'player' property (as in MainScene/BaseScene).
 */
export function registerInventoryAndPlayerListeners(
  scene: Phaser.Scene,
  windowManager: WindowManager,
) {
  if (!window.socket) return;


  // Listen for merchant-items response from server
  window.socket.on(
    "merchant-items",
    (data: {
      items: (ItemDefinition & { availableAt?: number })[];
      merchantId: number;
      merchantName: string;
      merchantLevel: number;
      fetchItems: { id: number; count?: number }[];
      fetchItemDefs: ItemDefinition[];
      canUpgrade: boolean;
    }) => {
      windowManager.showMerchantWindow(
        data.items,
        data.merchantId,
        data.merchantName,
        data.merchantLevel,
        data.fetchItems,
        data.fetchItemDefs,
        data.canUpgrade,
      );
    },
  );

  // Listen for squad-info response from server (squad created or loaded)
  window.socket.on(
    "squad-info",
    (data: { squadCode: string; members: string[]; memberDetails?: SquadMemberDetail[] }) => {
      if (windowManager) {
        windowManager.onSquadInfo(data.squadCode, data.members || [], data.memberDetails || []);
        // Update squad icon to show active state
        windowManager.updateSquadIcon();
        windowManager.showNotification(`Squad updated!`, "info");
      }
    },
  );

  // Listen for live squad-only updates (gear and/or match-status changes from a squad partner)
  window.socket.on("squad-update", (data: SquadUpdateData) => {
    if (windowManager) {
      windowManager.onSquadUpdate(data);
    }
  });

  // Listen for squad-left response from server
  window.socket.on("squad-left", () => {
    if (windowManager) {
      windowManager.onSquadLeft();
      // Update squad icon to show inactive state
      windowManager.updateSquadIcon();
    }
  });

  // Listen for squad-not-found response from server
  window.socket.on("squad-not-found", () => {
    // Squad window will show create/join buttons by default
    console.info("[CLIENT] No squad found for this account");
    windowManager.onSquadLeft();
    // Update squad icon to show inactive state
    windowManager.updateSquadIcon();
  });

  // Listen for squad-error response from server
  window.socket.on("squad-error", (data: { message: string }) => {
    console.error("[CLIENT] Squad error:", data.message);
    if (windowManager) {
      windowManager.onSquadError(data.message);
    }
  });

  // Helper function to update player data

  const updatePlayerData = (data: PlayerData) => {
    if (!window.player) {
      (window as any).player = { data: {} } as any;
    }

    // Update all player data properties, including name
    window.player.data = {
      ...window.player.data,
      ...data,
    };

    // Update active skin if provided
    if (data.activeSkin) {
      if (window.player.updateSkin) {
        window.player.updateSkin(data.activeSkin);
      }
    }

    // Update inventory icon if windowManager exists
    if (
      windowManager &&
      typeof windowManager.updateInventoryIcon === "function"
    ) {
      windowManager.updateInventoryIcon();
    } else {
      console.warn(
        "windowManager or updateInventoryIcon function not available",
      );
    }

    // Update weapon UI if windowManager exists
    if (
      windowManager &&
      windowManager.weaponUI &&
      typeof windowManager.weaponUI.refresh === "function"
    ) {
      windowManager.weaponUI.refresh();
    }

    // Update action slots instead of recreating them
    if (windowManager.hasActionSlots()) {
      windowManager.updateActionSlots();
    } else {
      // Only create action slots if they don't exist yet
      windowManager.showActionSlots();
    }

    // Only refresh if window is currently visible, otherwise just update data
    if (windowManager.isPlayerWindowVisible()) {
      windowManager.showPlayerWindow();
    }

    // Only refresh if window is currently visible, otherwise just update data
    if (windowManager.isInventoryWindowVisible()) {
      windowManager.showInventoryWindow();
    }
    
    // Update camera zoom based on weapon changes (if in MainScene)
    const isMainScene = scene && scene.scene && scene.scene.key === "MainScene";
    if (
      isMainScene &&
      typeof (scene as any).updateCameraZoomForWeapon === "function"
    ) {
      (scene as any).updateCameraZoomForWeapon();
    }
  };

  // Listen for player-data (full data with rooms - for initial setup)
  window.socket.on("player-data", (playerData: PlayerData) => {
    updatePlayerData(playerData);

    // Update weapon sprite when player data is received
    if (window.player && window.player.updateWeaponSprite) {
      window.player.updateWeaponSprite();
    }

    // Update skin if activeSkin is set
    if (window.player && window.player.updateSkin && playerData.activeSkin) {
      window.player.updateSkin(playerData.activeSkin);
    }

    // Update hat sprite when player data is received
    if (window.player && window.player.updateHatSprite) {
      window.player.updateHatSprite();
    }

    // Refresh armor bar to reflect equipped armor items
    if (window.player && window.player.updateArmorBar) {
      window.player.updateArmorBar();
    }

    // If BaseScene is active and storage tab is open, refresh storage window
    const gameInstance = (window as any).game as Phaser.Game;
    if (gameInstance?.scene) {
      const activeScenes = gameInstance.scene.scenes.filter((s) => s.scene.isActive());
      const baseScene = activeScenes.find((s) => s.scene.key === "BaseScene");
      if (baseScene && baseScene.storageScreen && typeof baseScene.storageScreen.refresh === "function") {
        if (baseScene.currentTabIndex === 2) {
          baseScene.storageScreen.refresh();
        }
      }
      if (baseScene && baseScene.deployScreen && typeof baseScene.deployScreen.refresh === "function") {
        if (baseScene.currentTabIndex === 4) {
          baseScene.deployScreen.refresh();
        }
      }
    }
  });

  // Storage items (player storage)
  window.socket.on(
    "storage-items",
    (data: { items: Item[] }) => {
      // Ensure window.player exists
      if (!window.player) {
        console.error("storage-items: window.player is not defined");
        return;
      }

      window.player.data.storage = (data.items || []) as any;

      // Only show storage/inventory windows if not in BaseScene
      const gameInstance = (window as any).game as Phaser.Game;
      let isBaseScene = false;
      if (gameInstance?.scene) {
        const activeScenes = gameInstance.scene.scenes.filter((s) => s.scene.isActive());
        if (activeScenes.length > 0) {
          isBaseScene = activeScenes[0].scene?.key === "BaseScene";
        }
      }
      if (!isBaseScene) {
        windowManager.showStorageWindow();
        windowManager.showInventoryWindow();
      }
    },
  );

  // Listen for loot-box-content from server and update/close loot window
  window.socket.on(
    "loot-box-content",
    (data: { x: number; y: number; items: Item[] }) => {
      if (windowManager) {
        // Refresh loot window with updated items (only if window is visible)
        windowManager.showLootWindow({
          x: data.x,
          y: data.y,
          items: data.items,
        });
      }
    },
  );

  // Listen for show-status-label message to display custom labels
  window.socket.on(
    "show-status-label",
    (data: { x: number; y: number; text: string; color: string }) => {
      // Get the current active scene - try multiple approaches
      let currentScene = null;
      // First try the stored scene
      if (scene?.add && scene?.time) {
        currentScene = scene;
      } else {
        // If the stored scene is invalid, try to get an active scene from the game
        const gameInstance = (window as any).game as Phaser.Game;
        if (gameInstance?.scene) {
          // Get all active scenes and use the first one
          const activeScenes = gameInstance.scene.scenes.filter((s) =>
            s.scene.isActive(),
          );
          if (activeScenes.length > 0) {
            const candidateScene = activeScenes[0];
            if (candidateScene?.add && candidateScene?.time) {
              currentScene = candidateScene;
            }
          }
        }
      }

      if (currentScene != null) {
        const sceneKey = currentScene.scene?.key;
        const canShowStatusLabel =
          sceneKey === "MainScene" || sceneKey === "MarketScene";

        // Status labels are gameplay UI and should not render in BaseScene.
        if (!canShowStatusLabel) {
          return;
        }

        // Use server coordinates by default
        let x = data.x;
        let y = data.y;

        // Fallback to player position when server coordinates are unavailable.
        const hasValidServerCoords =
          data.x !== undefined &&
          data.y !== undefined &&
          !(data.x === 0 && data.y === 0);

        if (!hasValidServerCoords) {
          if (
            window.player &&
            window.player.x !== undefined &&
            window.player.y !== undefined
          ) {
            x = window.player.x;
            y = window.player.y - 48; // Show above player head
          }
        }
        spawnPlayerLabel(data.text, x, y, currentScene, data.color);

        const soundGeo = {
          tileX: Math.floor(x / TILE_SIZE),
          tileY: Math.floor(y / TILE_SIZE),
        };

        // Play sound effects based on the label text
        if (data.text === "NO AMMO") {
          playSound(currentScene, "no-ammo-sound", soundGeo);
        } else if (data.text.startsWith("RELOADED")) {
          playSound(currentScene, "weapon-reload", soundGeo);
        } else if (data.text.startsWith("Bandage")) {
          playSound(currentScene, "bandage", soundGeo);
        } else if (data.text.startsWith("USED HEALTH PACK")) {
          playSound(currentScene, "heal", soundGeo);
        }
      }
    },
  );

  // Daily challenge kill progress
  window.socket.on(
    "daily-challenge-progress",
    (data: { kills: number; target: number; monster: string }) => {
      windowManager.showNotification(
        `${data.kills} / ${data.target} ${data.monster} killed`,
        "info",
        "DAILY CHALLENGE",
      );
    },
  );

  // Request player-data from server after listeners are registered
  window.socket.emit("request-player-data");
}

/**
 * Registers listeners for loot box management (remove and update sprite).
 * Works for both BaseScene and MainScene.
 */
export function registerLootBoxListeners(
  scene: BaseGameScene,
  windowManager: WindowManager,
) {
  if (!window.socket) return;

  // Remove loot box when server says it's gone
  window.socket.on("remove-loot-box", (data: { x: number; y: number }) => {
    if (!scene.mapHandler || !scene.mapHandler.lootBoxes) return;

    // Check if coordinates match any interactables (chests) - only in base scene
    const isBaseScene = scene.scene?.key === "BaseScene";
    if (isBaseScene && (scene as BaseScene).interactables) {
      const matchingInteractable = (scene as BaseScene).interactables.find(
        (inter: Interactable) => inter.x === data.x && inter.y === data.y,
      );
      if (matchingInteractable) {
        return;
      }
    }

    // Close loot window if it's showing this loot box
    if (windowManager) {
      windowManager.closeLootWindowIfMatches(data.x, data.y);
    }

    const idx = scene.mapHandler.lootBoxes.findIndex(
      (lb: LootBox) => lb.info.x === data.x && lb.info.y === data.y,
    );
    if (idx !== -1) {
      // Remove sprite if present
      const lootBox = scene.mapHandler.lootBoxes[idx];

      if (!lootBox.fixed) {
        lootBox.remove();
        scene.mapHandler.lootBoxes.splice(idx, 1);

        if (isBaseScene) {
          const baseScene: BaseScene = scene as BaseScene;
          // Clear highlighted loot box if it matches the removed loot box
          baseScene.interactionManager.clearLootBoxIfMatches(data.x, data.y);
        }
      }
    }

    // Hide OPEN button if no loot boxes are highlighted
    const highlightedLootBox = scene.interactionManager.getHighlightedLootBox();
    if (windowManager && !highlightedLootBox) {
      windowManager.hideOpenButton();
    }
  });

}
// NOTE: update-loot-bag-sprite is now handled by registerItemListeners in item-listeners.ts

/**
 * Unregisters loot box listeners.
 * NOTE: update-loot-bag-sprite cleanup is handled by unregisterItemListeners
 */
export function unregisterLootBoxListeners() {
  if (!window.socket) return;
  window.socket.off("remove-loot-box");
}

// Unregister inventory and player info listeners for a scene
export function unregisterInventoryAndPlayerListeners() {
  if (!window.socket) return;
  window.socket.off("storage-items");
  window.socket.off("loot-box-content");
  window.socket.off("show-status-label");
  window.socket.off("player-data");
  window.socket.off("shop-items");
  window.socket.off("crafting-recipes");
  window.socket.off("merchant-items");
  window.socket.off("squad-update");
  window.socket.off("squad-info");
  window.socket.off("squad-left");
  window.socket.off("squad-not-found");
  window.socket.off("squad-error");
  window.socket.off("daily-challenge-progress");
  unregisterLootBoxListeners();
}

// Send updated name to server
export function sendNameToServer(
  accountId: number,
  name: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!window.socket) return reject("No socket connection");
    window.socket.emit(
      "update-name",
      { accountId, name },
      (response: { success: boolean }) => {
        if (response && response.success) resolve();
        else reject("Failed to update name");
      },
    );
  });
}

/**
 * Set up disconnect handler for socket connection.
 * When the socket disconnects or has connection errors, attempts to reconnect to match if in MainScene.
 * Otherwise, returns to loading scene.
 */
export function registerDisconnectHandler(game: Phaser.Game) {
  if (!window.socket) return;

  // Listen for socket reconnection
  window.socket.on("connect", () => {
    console.info("[CLIENT] Socket connected");

    // Check if we were in a match when we disconnected
    if ((window as any).wasInMatch) {
      console.info("[CLIENT] Attempting to reconnect to match...");

      // Get the accountId from window
      const accountId = (window as any).accountId;
      if (accountId) {
        // Emit reconnection attempt to server
        window.socket.emit("attempt-reconnect", { accountId });
      } else {
        console.error("[CLIENT] No accountId found for reconnection");
        (window as any).wasInMatch = false;

        // Hide overlay and return to loading
        const mainScene = game.scene.getScene("MainScene") as any;
        if (mainScene?.hideReconnectingOverlay) {
          mainScene.hideReconnectingOverlay();
        }
        if (mainScene?.scene.isActive()) {
          mainScene.scene.stop();
        }
        game.scene.start("LoadingScene");
      }
    }
  });

  const handleDisconnect = () => {
    console.warn("[CLIENT] Socket disconnected");

    // Get current active scene
    const currentScene = game.scene.getScenes(true)[0];
    if (currentScene) {
      const sceneKey = currentScene.scene.key;

      // Don't redirect if already in LoadingScene or NameScene
      if (sceneKey === "LoadingScene" || sceneKey === "NameScene") {
        return;
      }

      // If in MainScene, send to LoadingScene for re-login
      if (sceneKey === "MainScene") {
        console.info(
          "[CLIENT] Disconnected from match. Returning to login for re-authentication",
        );

        const mainScene = currentScene as any;
        if (mainScene.scene.isActive()) {
          mainScene.scene.stop();
        }
        game.scene.start("LoadingScene");
        return;
      }

      // For other scenes, clean up and return to loading
      if (currentScene.scene.isActive()) {
        currentScene.scene.stop();
      }

      // Start loading scene
      game.scene.start("LoadingScene");
    }
  };

  // Listen for disconnect event
  window.socket.on("disconnect", handleDisconnect);

  // Listen for connection errors
  window.socket.on("connect_error", (error: Error) => {
    console.error("[CLIENT] Connection error:", error);
    handleDisconnect();
  });

  // Listen for error event
  window.socket.on("error", (error: Error) => {
    console.error("[CLIENT] Socket error:", error);
    handleDisconnect();
  });

  // Listen for reconnection failure
  window.socket.on("reconnect-failed", (data: { reason: string }) => {
    console.warn("[CLIENT] Reconnection failed:", data.reason);
    (window as any).wasInMatch = false;

    // Get MainScene and hide overlay
    const mainScene = game.scene.getScene("MainScene") as any;
    if (mainScene && mainScene.hideReconnectingOverlay) {
      mainScene.hideReconnectingOverlay();
    }

    // Return to loading scene
    if (mainScene && mainScene.scene.isActive()) {
      mainScene.scene.stop();
    }
    game.scene.start("LoadingScene");
  });

  // Listen for reconnection success
  window.socket.on(
    "reconnect-to-match",
    (data: {
      position: any;
      health: number;
      maxHealth: number;
      armor?: number;
      maxArmor?: number;
      selectedMap?: string;
    }) => {
      console.info("[CLIENT] Reconnected to match successfully");
      (window as any).wasInMatch = false;

      // Store selected map for reconnection
      if (data.selectedMap) {
        (window as any).selectedMap = data.selectedMap;
      }

      // Check if MainScene exists and is active
      const mainScene = game.scene.getScene("MainScene") as any;
      const isMainSceneActive = mainScene && mainScene.scene.isActive();

      if (isMainSceneActive) {
        // MainScene is already active, just restore player state
        if (mainScene.hideReconnectingOverlay) {
          mainScene.hideReconnectingOverlay();
        }

        if (mainScene.player) {
          // Restore player position and health
          if (data.position) {
            mainScene.player.setPositionInstant(
              data.position.x,
              data.position.y,
            );
          }
          if (data.health !== undefined && data.maxHealth !== undefined) {
            mainScene.player.updateHealth(data.health, data.maxHealth, data.armor, data.maxArmor);
          }
        }

        // Request fresh game state
        window.socket.emit("player-enter-main");
      } else {
        // MainScene is not active, need to start it
        console.info("[CLIENT] Starting MainScene to rejoin match");

        // Stop any active scenes first
        const activeScenes = game.scene.getScenes(true);
        activeScenes.forEach((scene) => {
          if (scene.scene.key !== "MainScene") {
            scene.scene.stop();
          }
        });

        // Store the reconnection data for MainScene to use
        (window as any).reconnectData = data;

        // Start MainScene - it will pick up the reconnection data
        game.scene.start("MainScene");
      }
    },
  );

  // Listen for reconnection failure
  window.socket.on("reconnect-failed", (data: { reason: string }) => {
    console.warn("[CLIENT] Reconnection failed:", data.reason);
    (window as any).wasInMatch = false;

    // Get current active scene
    const currentScene = game.scene.getScenes(true)[0];
    if (currentScene) {
      const sceneKey = currentScene.scene.key;

      // Return to loading scene if not already there
      if (sceneKey !== "LoadingScene" && sceneKey !== "NameScene") {
        if (currentScene.scene.isActive()) {
          currentScene.scene.stop();
        }
        game.scene.start("LoadingScene");
      }
    }
  });
}

/**
 * Remove disconnect handlers from socket.
 */
export function unregisterDisconnectHandler() {
  if (!window.socket) return;
  window.socket.off("disconnect");
  window.socket.off("connect_error");
  window.socket.off("error");
  window.socket.off("reconnect-to-match");
  window.socket.off("reconnect-failed");
}

/**
 * Connection quality monitoring
 */
let connectionQualityMonitor: {
  enabled: boolean;
  checkIntervalMs: number;
  lastPingTime: number;
  averagePing: number;
  badConnectionThresholdMs: number;
  monitoringInterval?: NodeJS.Timeout;
  onQualityChange?: (isBadConnection: boolean) => void;
} = {
  enabled: false,
  checkIntervalMs: 3000, // Check connection every 3 seconds
  lastPingTime: 0,
  averagePing: 0,
  badConnectionThresholdMs: 1000, // Consider connection bad if ping > 1000ms
};

/**
 * Start monitoring connection quality by measuring ping times
 * @param onQualityChange - Callback when connection quality changes
 */
export function startConnectionQualityMonitoring(
  onQualityChange?: (isBadConnection: boolean) => void,
) {
  if (!window.socket || connectionQualityMonitor.enabled) return;

  connectionQualityMonitor.enabled = true;
  connectionQualityMonitor.onQualityChange = onQualityChange;
  connectionQualityMonitor.averagePing = 0;

  const checkConnection = () => {
    if (!window.socket || !window.socket.connected) {
      connectionQualityMonitor.enabled = false;
      if (connectionQualityMonitor.monitoringInterval) {
        clearInterval(connectionQualityMonitor.monitoringInterval);
      }
      return;
    }

    const startTime = Date.now();

    // Use socket.emit with callback to measure round-trip time
    window.socket.emit("ping", (response: any) => {
      const endTime = Date.now();
      const pingTime = endTime - startTime;

      // Calculate moving average (weighted towards recent pings)
      connectionQualityMonitor.averagePing =
        connectionQualityMonitor.averagePing * 0.7 + pingTime * 0.3;

      const isBadConnection =
        connectionQualityMonitor.averagePing >
        connectionQualityMonitor.badConnectionThresholdMs;

      if (onQualityChange) {
        onQualityChange(isBadConnection);
      }
    });
  };

  // Start checking connection quality
  connectionQualityMonitor.monitoringInterval = setInterval(
    checkConnection,
    connectionQualityMonitor.checkIntervalMs,
  );

  // Initial check after a short delay
  setTimeout(checkConnection, 500);
}

/**
 * Stop monitoring connection quality
 */
export function stopConnectionQualityMonitoring() {
  if (connectionQualityMonitor.monitoringInterval) {
    clearInterval(connectionQualityMonitor.monitoringInterval);
  }
  connectionQualityMonitor.enabled = false;
  connectionQualityMonitor.onQualityChange = undefined;
}

/**
 * Get current average ping time
 */
export function getAveragePing(): number {
  return Math.round(connectionQualityMonitor.averagePing);
}
